<template>
  <vx-card title="Rekonsiliasi Saldo">
    <p class="mb-6">Periksa saldo kas atau bank dari saldo awal dan seluruh item voucher. Perbaikan menghitung ulang debit, credit, saldo berjalan, dan nomor urut bulanan.</p>
    <p class="mb-6">Nominal negatif dan nol pada item historis tetap dihitung sesuai nilai aslinya. Deskripsi kosong tidak mengubah nominal transaksi.</p>
    <div class="reconciliation-fields">
      <label>Company
        <select v-model="companyId" :disabled="busy" @change="loadCompany">
          <option value="" disabled>Pilih company</option>
          <option v-for="company in companies" :key="company.id" :value="company.id">{{ company.name }}</option>
        </select>
      </label>
      <label>Jenis buku
        <select v-model="type" :disabled="busy" @change="resetOwner">
          <option value="cash">Kas</option><option value="bank">Bank</option>
        </select>
      </label>
      <label>{{ type === 'cash' ? 'Cashbook' : 'Rekening bank' }}
        <select v-model="ownerId" :disabled="busy || !companyId" @change="result = null">
          <option value="" disabled>Pilih buku yang akan diperiksa</option>
          <option v-for="owner in owners" :key="owner.id" :value="owner.id">{{ type === 'cash' ? owner.name : `${owner.account_number} (${owner.currency_type})` }}</option>
        </select>
      </label>
    </div>
    <p v-if="companyId && !busy && !owners.length" class="mb-4">Belum ada buku untuk pilihan ini.</p>
    <p v-if="error" role="alert" class="text-danger mb-4">{{ error }}</p>
    <vs-button :disabled="busy || !ownerId" @click="check">{{ busy ? 'Memproses...' : 'Periksa saldo' }}</vs-button>
    <section v-if="result" class="reconciliation-result mt-6" aria-live="polite">
      <h3 class="mb-4">{{ result.applied ? 'Perbaikan berhasil diterapkan' : 'Hasil pemeriksaan — belum disimpan' }}</h3>
      <table>
        <tbody>
          <tr><th>Saldo awal</th><td>{{ money(result.opening_balance) }}</td></tr>
          <tr><th>Saldo akhir tersimpan{{ result.applied ? ' sebelumnya' : '' }}</th><td>{{ money(result.stored_balance) }}</td></tr>
          <tr><th>Saldo akhir hasil perhitungan</th><td>{{ money(result.calculated_balance) }}</td></tr>
          <tr><th>Voucher diperiksa</th><td>{{ result.voucher_count }}</td></tr>
          <tr><th>Voucher {{ result.applied ? 'diperbaiki' : 'perlu diperbaiki' }}</th><td>{{ result.changed_vouchers }}</td></tr>
        </tbody>
      </table>
      <p v-if="!hasChanges && !result.applied" class="mt-4">Saldo dan urutan voucher sudah sesuai.</p>
      <vs-button v-if="hasChanges && !result.applied" class="mt-4" color="warning" :disabled="busy" @click="confirmApply">Terapkan perbaikan</vs-button>
    </section>
  </vx-card>
</template>

<script>
import axios from '@/axios.js'

export default {
  data () {
    return { companies: [], cashbooks: [], banks: [], companyId: '', type: 'cash', ownerId: '', busy: false, error: '', result: null }
  },
  computed: {
    owners () { return this.type === 'cash' ? this.cashbooks : this.banks },
    hasChanges () { return this.result && (this.result.balance_changed || this.result.changed_vouchers > 0) }
  },
  created () { this.loadCompany() },
  methods: {
    money (value) {
      // Preserve integer precision for balances beyond JavaScript's safe number range.
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    resetOwner () { this.ownerId = ''; this.result = null; this.error = '' },
    showError (error) {
      const data = error.response && error.response.data
      this.error = data && data.errors ? Object.values(data.errors).flat().join(' ') : (data && data.message) || 'Proses gagal. Silakan coba lagi.'
    },
    async loadCompany () {
      this.resetOwner()
      this.cashbooks = []; this.banks = []
      this.busy = true
      try {
        const response = await axios.get('/balance-reconciliation/options', { params: { company_id: this.companyId || undefined } })
        this.companies = response.data.data.companies
        this.cashbooks = response.data.data.cashbooks
        this.banks = response.data.data.banks
      } catch (error) { this.showError(error) } finally { this.busy = false }
    },
    check () { this.reconcile(false) },
    confirmApply () {
      const selection = `${this.companyId}/${this.type}/${this.ownerId}`
      const token = this.result.preview_token
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Terapkan rekonsiliasi?',
        text: 'Saldo dan nomor urut voucher pada buku terpilih akan diperbarui sesuai hasil pemeriksaan.',
        acceptText: 'Terapkan',
        accept: () => {
          if (selection === `${this.companyId}/${this.type}/${this.ownerId}` && this.result && this.result.preview_token === token) this.reconcile(true)
        }
      })
    },
    async reconcile (apply) {
      if (this.busy || !this.ownerId) return
      this.busy = true; this.error = ''
      const token = apply && this.result ? this.result.preview_token : null
      this.result = null
      try {
        const response = await axios.post('/balance-reconciliation', {
          company_id: this.companyId, owner_id: this.ownerId, type: this.type, apply, preview_token: token
        })
        this.result = response.data.data
      } catch (error) { this.showError(error) } finally { this.busy = false }
    }
  }
}
</script>

<style scoped>
.reconciliation-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-bottom: 24px; }
.reconciliation-fields label { display: flex; flex-direction: column; gap: 8px; }
.reconciliation-fields select { padding: 12px; border: 1px solid #cbd5e1; border-radius: 6px; background: transparent; color: inherit; width: 100%; }
.reconciliation-result { overflow-x: auto; }
table { width: 100%; max-width: 700px; border-collapse: collapse; }
th, td { padding: 12px; border-bottom: 1px solid #e2e8f0; text-align: left; }
td { text-align: right; font-variant-numeric: tabular-nums; }
</style>
